// Qui peut désigner ou retirer la meilleure réponse d'une discussion.
//
// Ces règles doublent celles de InterfaceController::bestMessage côté back
// (repo believemy_v3). Le back reste seul juge : ce fichier sert à n'afficher
// que les actions qu'il accepterait, pour qu'un bouton visible ne finisse
// jamais en 403. Toute évolution de l'un se reporte sur l'autre.

const ADMIN_ROLES = ["ROLE_ADMIN", "ROLE_SUPER_ADMIN"];

export const isPlatformAdmin = (roles) =>
    (roles || []).some((role) => ADMIN_ROLES.includes(role));

// L'instructeur se trouve derrière la formation, ou derrière le module quand
// la session appartient à un accélérateur. Même résolution que
// InterfaceController::resolveSessionInstructor().
export const getDiscussionInstructorId = (discussion) =>
    discussion?.session?.part?.course?.instructor?.id ??
    discussion?.session?.part?.programModule?.program?.instructor?.id ??
    null;

// Où se trouve la discussion, pour les liens que les routes Next glissent dans
// leurs e-mails. Une session d'accélérateur n'a pas de `course` : la lire sans
// repli faisait tomber la page au clic.
export const getDiscussionLocation = (discussion) => {
    const part = discussion?.session?.part;
    const product = part?.course || part?.programModule?.program || null;

    return {
        slug: product?.slug ?? null,
        partNumber: part?.number ?? null,
        sessionNumber: discussion?.session?.position ?? null,
        moduleNumber: part?.programModule?.number ?? null,
    };
};

/**
 * Désigner une réponse comme la meilleure.
 *
 * @param {object}  discussion  la discussion normalisée (groupe "discussion")
 * @param {object}  answer      la réponse visée
 * @param {number}  userId      session.user.userId
 * @param {array}   roles       session.user.roles
 * @param {array}   [answers]   les réponses de la discussion, quand elles sont
 *                              connues : le back refuse une seconde meilleure
 *                              réponse à qui n'est pas administrateur
 */
export const canMarkBestAnswer = ({
    discussion,
    answer,
    userId,
    roles,
    answers = null,
}) => {
    if (!discussion || !answer || !userId) return false;

    // Une conversation privée n'a pas de meilleure réponse.
    if (discussion.isPrivate) return false;

    // Déjà désignée : seul un administrateur revient dessus.
    if (answer.bestMessage) return false;

    if (isPlatformAdmin(roles)) return true;

    // Personne ne s'attribue les points de sa propre réponse.
    if (answer.user?.id === userId) return false;

    // Une seule meilleure réponse par discussion, l'admin seul peut la déplacer.
    if (answers && answers.some((existing) => existing.bestMessage)) return false;

    const isDiscussionAuthor = discussion.author?.id === userId;
    const isInstructor = getDiscussionInstructorId(discussion) === userId;

    return isDiscussionAuthor || isInstructor;
};

/**
 * Retirer la meilleure réponse. Réservé aux administrateurs côté back, parce
 * que le retrait reprend les 50 XP accordés à son auteur.
 */
export const canRemoveBestAnswer = ({ discussion, answer, roles }) => {
    if (!discussion || !answer) return false;
    if (discussion.isPrivate) return false;

    return Boolean(answer.bestMessage) && isPlatformAdmin(roles);
};
